import React, {FC, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Baseline, Grid, Row, InvisibleContainer, GridSpan} from '../../Layout/Grid'
import {LabelInput, Select, LabelCheckbox} from '../../Atoms/Forms'
import {Button, ButtonRowGrid, ButtonRowWrap} from '../../Atoms/Buttons'
import {useUserAccountDetailsForm} from '../../../config/userAccountUtils'
import EditUserAccountSettingsUserGroups from './EditUserAccountSettingsUserGroups'
import {User, UserRole} from '../../../state/state'
import {H2, H3, H6, P, Text2} from '../../Atoms/Typography'
import {IconButton} from '../../Atoms/Buttons'
import {IconCollapse, IconExpand} from '../../../Assets/Icons/TinyIcons'
import {LargeProfileImageFromAsset} from '../../Atoms/ProfileImageLoader'
import {GoBackHeaderTitle, HeaderRow, HeaderActions} from '../../Molecules/ViewComponents/ViewHeader'
import {useAppState} from '../../../state'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'
import {isEmpty} from 'lodash'
import {MainLayoutWithoutStretch, MainContent, NarrowRightSidebar, AdjustableContainer} from '../../Layout/Layout'
import {colors} from '../../../sharedComponents/colors'
import AnchorModal, {TinyModalContent} from '../../Molecules/Modals/AnchorModal'
import ConfirmModal from '../../Molecules/Modals/ConfirmModal'
import {valueExists} from '../../../config/utils'
import AssetUploaderSmall from '../../Molecules/UploadImage'
import {ScheduledReportType, UserLanguage} from '../../../state/rest'
import {AnyOrgLevel, OffsetCheckboxList, availableKeyValues, parseLevel, role2level} from '../Utils'
import {IconPalette, SharedIcon, Text} from '../../../sharedComponents/components'
import RadioButtonGroup from '../../Atoms/RadioButtonGroup'
import {ColoredTogglerButton} from '../../Atoms/ColoredTogglerButton'
import styled from 'styled-components'

const WelcomeBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  margin-bottom: 8px;
  background-color: ${colors.brand.midGreen ?? '#e8f4fd'};
  border-left: 4px solid ${colors.brand.midGreen ?? '#0078d4'};
  border-radius: 4px;
`

const BannerText = styled.div`
  flex: 1;
`

const BannerTitle = styled.p`
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${colors.system.white ?? '#333'};
`

const BannerDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.system.white ?? '#333'};
`

const BannerCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: ${colors.system.white ?? '#333'};
  padding: 0;
  flex-shrink: 0;
  &:hover {
    opacity: 0.7;
  }
`

const EditDetailsGrid = styled(Grid)`
  grid-template-columns: 1fr;

  @media (min-width: 1200px) {
    grid-template-columns: minmax(640px, 640px) minmax(340px, 340px);
    justify-content: space-between;
  }
`

const ReportSubscriptionsColumn = styled.div`
  @media (min-width: 1200px) {
    grid-column: 2;
  }
`

const collectAllSiteIds = (node: AnyOrgLevel): string[] => {
  const keys = Object.keys(node) as Array<keyof AnyOrgLevel>
  const arr = keys.find(k => availableKeyValues.includes(k)) as 'locationGroups' | 'locations' | 'brands' | 'sites'
  const children = node[arr]
  if (!children || children.length === 0) {
    return [node.id]
  }
  return children.flatMap(c => collectAllSiteIds(c))
}

interface USSProps {
  data: AnyOrgLevel
  selected: string[]
  onToggle: (siteIds: string[]) => void
  disableChildren?: boolean
}
const UserSiteSelector = ({data, selected, onToggle, disableChildren = false}: USSProps) => {
  const keys = Object.keys(data) as Array<keyof AnyOrgLevel>
  const arr = keys.find(k => availableKeyValues.includes(k)) as 'locationGroups' | 'locations' | 'brands' | 'sites'
  const nextLevel = data[arr]

  if (nextLevel) {
    const allDescendantSiteIds = collectAllSiteIds(data)
    const allChecked = allDescendantSiteIds.length > 0 && allDescendantSiteIds.every(id => selected.includes(id))
    const isChecked = allChecked || disableChildren

    if (data.visible !== false) {
      return (
        <>
          <LabelCheckbox
            checked={isChecked}
            disabled={disableChildren}
            label={data.name}
            name={data.name}
            flipped={false}
            onClick={() => !disableChildren && onToggle(allDescendantSiteIds)}
          />
          <OffsetCheckboxList>
            {nextLevel.map(n => (
              <li key={n.id}>
                <UserSiteSelector data={n} selected={selected} onToggle={onToggle} disableChildren={isChecked} />
              </li>
            ))}
          </OffsetCheckboxList>
        </>
      )
    } else
      return (
        <>
          {nextLevel.map(n => (
            <UserSiteSelector data={n} selected={selected} onToggle={onToggle} disableChildren={disableChildren} />
          ))}
        </>
      )
  } else {
    const isChecked = selected.includes(data.id) || disableChildren
    return (
      <LabelCheckbox
        checked={isChecked}
        disabled={disableChildren}
        label={data.name}
        name={data.name}
        flipped={false}
        onClick={() => !disableChildren && onToggle([data.id])}
      />
    )
  }
}
interface LCProps {
  level: string
  onlySites: boolean
  onClick: (level: UserRole, org: AnyOrgLevel) => void
  data: AnyOrgLevel
  orgState: OrgState[]
  disableNextLevel: boolean
  writableIds: string[]
}
const ManagerLevelCheckbox = ({data, level, onlySites, onClick, orgState, disableNextLevel, writableIds}: LCProps) => {
  const keys = Object.keys(data) as Array<keyof AnyOrgLevel>
  const arr = keys.find(k => availableKeyValues.includes(k)) as 'locationGroups' | 'locations' | 'brands' | 'sites'
  const inState = !!orgState.find(os => os.id === data.id)
  const checked = inState || disableNextLevel
  const showCheckbox = writableIds.includes(data.id)
  if (data.visible !== false) {
    return (
      <>
        {showCheckbox ? (
          <LabelCheckbox
            disabled={disableNextLevel}
            checked={checked}
            flipped={false}
            label={data.name}
            name={arr}
            onClick={() => onClick(level as UserRole, data)}
          />
        ) : (
          <P>{data.name}</P>
        )}
        {!!arr && (
          <OffsetCheckboxList>
            {data[arr]!.map(item => (
              <li key={item.id}>
                <ManagerLevelCheckbox
                  writableIds={writableIds}
                  orgState={orgState}
                  data={item}
                  level={arr}
                  onlySites={onlySites}
                  onClick={onClick}
                  disableNextLevel={checked}
                />
              </li>
            ))}
          </OffsetCheckboxList>
        )}
      </>
    )
  } else {
    const items = data[arr]!
    return (
      <>
        {items.map(item => (
          <ManagerLevelCheckbox
            writableIds={writableIds}
            orgState={orgState}
            data={item}
            level={arr}
            onlySites={onlySites}
            onClick={onClick}
          />
        ))}
      </>
    )
  }
}

ManagerLevelCheckbox.defaultProps = {
  disableNextLevel: false
}

interface FormProps {
  savedData?: User
  isEditingSelf: boolean
}

export interface OrgState {
  level: string
  id: string
}
function resolveManagerRole(level: UserRole) {
  return level !== 'BU' && level !== 'HI'
}

function childIds(data: AnyOrgLevel, first: boolean = false): string[] {
  const keys = Object.keys(data) as Array<keyof AnyOrgLevel>
  const arr = keys.find(k => Array.isArray(data[k])) as 'locationGroups' | 'locations' | 'brands' | 'sites'
  const level = data[arr]

  if (level) {
    return first ? [...level.flatMap(l => childIds(l))] : [data.id, ...level.flatMap(l => childIds(l))]
  } else return [data.id]
}

const reportOptions: Array<{type: ScheduledReportType; labelKey: string; fallback: string}> = [
  {
    type: 'SUMMARY_REPORT',
    labelKey: 'users:labels.reportSummary',
    fallback: 'Summary'
  },
  /* {
    type: 'WASTE_COMPARISON_REPORT',
    labelKey: 'users:labels.reportWasteComparison',
    fallback: 'Waste comparison'
  } */
]

const EditUserAccountSettingsDetails: FC<FormProps> = ({savedData, isEditingSelf}: FormProps): any => {
  const {t} = useTranslation(['assets', 'users', 'settings', 'common'])
  const {state, effects} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()

  const backAddress = isEditingSelf ? '/settings' : '/settings/users'
  const backLabel = isEditingSelf
    ? t('settings:labels.settingsTitle', 'Settings')
    : t('settings:labels.userAccountSettings', 'User account settings')
  const fc = useUserAccountDetailsForm({saved: savedData, backAddress})

  const [selectedRole, setSelectedRole] = useState<string>(() => {
    if (savedData) {
      const userRoles = savedData.userToSite.map(uts => uts.userRole)
      if (userRoles.includes('BU') || userRoles.includes('HI')) {
        return userRoles[0]
      }
      return 'LM'
    }
    return 'BU'
  })

  const [selectedBuSites, setSelectedBUSites] = useState<string[]>(() => {
    if (savedData) {
      return savedData.userToSite.filter(uts => uts.userRole === 'BU' || uts.userRole === 'HI').map(uts => uts.siteId)
    }
    return []
  })

  const [showWelcomeBanner, setShowWelcomeBanner] = useState(() => {
    if (!isEditingSelf) return false
    const val = sessionStorage.getItem('showNewUserWelcome')
    if (val) sessionStorage.removeItem('showNewUserWelcome')
    return val === 'true'
  })
  const [selectedReportTypes, setSelectedReportTypes] = useState<ScheduledReportType[]>([])
  const [isLoadingReportSubscriptions, setIsLoadingReportSubscriptions] = useState(false)

  // Handler to toggle one or more site ids (supports single site and group-level bulk toggle)
  const handleBUToggle = (siteIds: string[]) => {
    // Only keep ids that are actual site ids (filter out location/brand ids that have no sites)
    const actualSiteIds = siteIds.filter(id => isSiteId(id))
    setSelectedBUSites(prevSelected => {
      const allSelected = actualSiteIds.every(id => prevSelected.includes(id))
      if (allSelected) {
        return prevSelected.filter(id => !actualSiteIds.includes(id))
      } else {
        const newSet = new Set(prevSelected)
        actualSiteIds.forEach(id => newSet.add(id))
        return Array.from(newSet)
      }
    })
  }

  // Helper to determine whether an id corresponds to a site in the chain tree
  const isSiteId = (id: string): boolean => {
    const visit = (node: AnyOrgLevel): boolean => {
      const keys = Object.keys(node) as Array<keyof AnyOrgLevel>
      const arr = keys.find(k => availableKeyValues.includes(k)) as
        | 'locationGroups'
        | 'locations'
        | 'brands'
        | 'sites'
        | undefined
      if (!arr) return false
      const children = node[arr]
      if (!children) return false
      for (const child of children) {
        if (child.id === id) {
          return arr === 'sites'
        }
        if (visit(child)) return true
      }
      return false
    }
    return visit(chain)
  }

  const passwordErrorMessage: string | undefined =
    fc.form.confirmNewPassword !== fc.form.newPassword
      ? t('common:validation.passwordsNotMatching', 'New passwords must match')
      : fc.form.newPassword && fc.form.newPassword.length < 8
      ? t('common:validation.passwordTooShort', 'New password is too short, must be at least 8 characters')
      : fc.form.newPassword && (!/[a-z]/.test(fc.form.newPassword) || !/[A-Z]/.test(fc.form.newPassword))
      ? t('common:validation.passwordFormatWrong', 'New password must contain both lowercase and uppercase letters')
      : undefined

  const originalEmail = (savedData && savedData.email) || ''
  const emailChanged = originalEmail !== fc.form.email
  const emailErrorMessage =
    emailChanged && fc.form.email !== fc.form.confirmEmail
      ? t('common:validation.emailMustMatch', 'Email addresses must match')
      : undefined

  const [isManager, setIsManager] = useState(savedData ? resolveManagerRole(savedData.userToSite[0].userRole) : false)
  const isEditing = !!savedData
  const isSuperuser = !!state.me?.accessRights.superuser

  useEffect(() => {
    if (!savedData?.id || !isSuperuser) {
      return
    }

    const loadReportSubscriptions = async () => {
      try {
        setIsLoadingReportSubscriptions(true)
        const data = await effects.userApi.getUserReportSubscriptions(savedData.id)
        setSelectedReportTypes(data.reportTypes || [])
      } catch (err) {
        console.error('Failed to load report subscriptions', err)
      } finally {
        setIsLoadingReportSubscriptions(false)
      }
    }

    loadReportSubscriptions()
  }, [effects.userApi, isSuperuser, savedData?.id])

  const toggleReportType = (reportType: ScheduledReportType, enabled: boolean) => {
    setSelectedReportTypes((prev) => {
      if (enabled) {
        return prev.includes(reportType) ? prev : [...prev, reportType]
      }
      return prev.filter((type) => type !== reportType)
    })
  }

  function handleOrgState(data?: User) {
    if (data) {
      return data.userToSite
        .filter(uts => uts.userRole !== 'BU' && uts.userRole !== 'HI')
        .map(uts => ({level: role2level(uts.userRole), id: uts[parseLevel(uts.userRole)]}))
    } else return []
  }
  const handleManagementLevelClick = (level: UserRole, org: AnyOrgLevel) => {
    const newState: OrgState[] = !!orgState.find(orgLevel => orgLevel.id === org.id)
      ? orgState.filter(orgLevel => orgLevel.id !== org.id)
      : [...orgState, {level, id: org.id}]
    const ids = childIds(org, true)
    const removeThese = orgState.filter(s => ids.includes(s.id))
    setOrgState(newState.filter(st => !removeThese.includes(st)))
  }

  const [orgState, setOrgState] = useState<OrgState[]>(handleOrgState(savedData))

  const handleRoleChange = (value: string) => {
    setSelectedRole(value)
    setIsManager(value === 'LM')
    if (value === 'HI' || value === 'BU') {
      setSelectedBUSites([])
    } else {
      setOrgState([])
    }
  }
  const rolesEmpty = isEmpty(orgState) && isEmpty(selectedBuSites)

  const {me} = useAppState().state
  const orgLevels = Object.keys(me!.accessRights.write) as Array<'brands' | 'locations' | 'chains' | 'groups' | 'sites'>
  const writableIds = orgLevels.flatMap(level => me!.accessRights.write[level])
  const chain = state.chainsById[state.selectedChainId as keyof typeof state.chainsById]
  // Helper: find a node in the org tree by id
  const findNodeById = (node: AnyOrgLevel | undefined, id: string): AnyOrgLevel | null => {
    if (!node) return null
    if (node.id === id) return node
    const keys = Object.keys(node) as Array<keyof AnyOrgLevel>
    const arr = keys.find(k => availableKeyValues.includes(k)) as
      | 'locationGroups'
      | 'locations'
      | 'brands'
      | 'sites'
      | undefined
    if (!arr) return null
    const children = node[arr]
    if (!children) return null
    for (const child of children) {
      const found = findNodeById(child, id)
      if (found) return found
    }
    return null
  }

  // Expand an OrgState entry into actual site ids; if the node has no descendant sites and is not itself a site,
  // return an empty array (we do not want to send non-site ids to the backend).
  const siteIdsFromOrgEntry = (entry: OrgState): string[] => {
    const node = findNodeById(chain, entry.id)
    if (!node) return []
    const descendantIds = collectAllSiteIds(node)
    if (descendantIds.length === 1 && descendantIds[0] === node.id) {
      return entry.level === 'sites' ? [node.id] : []
    }
    return descendantIds
  }

  // Build final list of site ids only
  const siteIdsOnly: string[] = Array.from(
    new Set([...selectedBuSites, ...orgState.flatMap(s => siteIdsFromOrgEntry(s))])
  )
  const showResetPassword =
    (state.me?.accessRights.superuser || writableIds.length > 0) &&
    isEditing &&
    !isEditingSelf &&
    !savedData?.ssoProvisioned
  const showPhotoUpload = !state.me?.accessRights.superuser
  const [isOpen, setOpen] = useState(false)
  const onDelete = async () => {
    if (savedData) {
      fc.remove(savedData.id)
    }
  }
  const [modalOpen, setModalOpen] = useState(false)
  const orgIdLevels = ['siteId', 'locationId', 'locationGroupId', 'brandId', 'chainId'] as Array<
    'siteId' | 'locationId' | 'locationGroupId' | 'brandId' | 'chainId'
  >
  const usersLocationIds = orgIdLevels.flatMap(level =>
    savedData?.userToSite.map(uts => uts[level]).filter(l => l !== null)
  )

  const isEditingAllowed =
    isEditingSelf ||
    state.me?.accessRights.superuser ||
    !isEditing ||
    usersLocationIds?.filter(uli => !writableIds.includes(uli || '')).length === 0

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i
  return (
    <MainLayoutWithoutStretch>
      <ConfirmModal
        heading={t('users:labels.confirmDeleteAccount', 'Delete user account?')}
        subHeading={`${savedData?.firstName} ${savedData?.lastName}`}
        content={t(
          'users:messages.confirmDelete',
          "Are you sure you want to delete this user account? If you choose to proceed, the user account and it's content will be removed"
        )}
        confirm={t('users:labels.confirmCheckbox', 'I confirm that I want to delete user account')}
        actions={[
          {
            label: t('common:actions.cancel', 'Cancel'),
            action: () => setModalOpen(false)
          },
          {
            label: t('common:actions.delete', 'Delete'),
            action: () => onDelete()
          }
        ]}
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
      />
      <MainContent variant="white">
        <Baseline>
          <HeaderRow
            modal={
              !isEditingSelf && (
                <AnchorModal anchorMode={'right'} isOpen={isOpen} onClose={() => setOpen(false)}>
                  <TinyModalContent data-cy="task-menu-modal">
                    <Baseline>
                      <IconButton
                        description={t('users:actions.deleteUser', 'Delete user')}
                        buttonProps={{
                          onClick: () => {
                            setOpen(false)
                            setModalOpen(true)
                          }
                        }}
                      />
                    </Baseline>
                  </TinyModalContent>
                </AnchorModal>
              )
            }
          >
            <GoBackHeaderTitle
              label={
                isEditing
                  ? isEditingSelf
                    ? `${t('users:actions.editYourAccount', 'My profile settings')}`
                    : `${t('users:actions.editAccount', 'Edit user account')}`
                  : `${t('users:actions.addAccount', 'Add user account')}`
              }
              path={backAddress}
              backLabel={backLabel}
            />
            {isEditing && !isEditingSelf && isEditingAllowed && <HeaderActions onDelete={() => setModalOpen(true)} />}
          </HeaderRow>

          <InvisibleContainer>
            {showWelcomeBanner && (
              <WelcomeBanner>
                <BannerText>
                  <BannerTitle>{t('users:newUser.welcomeTitle', 'Welcome to Chefstein! 👋')}</BannerTitle>
                  <BannerDescription>
                    {t(
                      'users:newUser.welcomeDescription',
                      'Please fill in your first and last name to complete your profile setup.'
                    )}
                  </BannerDescription>
                </BannerText>
                <BannerCloseButton onClick={() => setShowWelcomeBanner(false)}>✕</BannerCloseButton>
              </WelcomeBanner>
            )}
            <EditDetailsGrid>
              <Baseline>
                {showPhotoUpload ? (
                  <>
                    <Row childMargin="1rem">
                      {!fc.asset ? (
                        <SharedIcon icon={IconPalette.Profile} />
                      ) : (
                        <LargeProfileImageFromAsset asset={fc.asset} />
                      )}
                      <Text size="M">{t('common:general.profilePicture', 'Your profile picture')}</Text>
                    </Row>
                    <Row>
                      <AssetUploaderSmall
                        onAssetSaved={isHealthInspector ? (asset?: any) => {} : fc.onAssetSaved}
                        savedAsset={fc.asset}
                        disabled={isHealthInspector}
                        {...(isHealthInspector ? {maxFiles: 0} : {})}
                      />
                    </Row>
                  </>
                ) : null}
                <Row childMargin="1rem">
                  <LabelInput
                    placeholder={t('common:general.firstName', 'First name')}
                    autoComplete="given-name"
                    name="firstName"
                    id="firstName"
                    labelText={t('common:general.firstName', 'First name')}
                    ref={fc.register({required: true})}
                    disabled={isHealthInspector}
                    errorMsg={
                      !!fc.errors.firstName
                        ? t('common:validation.firstNameMissing', 'First name is mandatory')
                        : undefined
                    }
                  />
                  <LabelInput
                    placeholder={t('common:general.lastName', 'Last name')}
                    autoComplete="family-name"
                    name="lastName"
                    id="lastName"
                    labelText={t('common:general.lastName', 'Last name')}
                    ref={fc.register({required: true})}
                    disabled={isHealthInspector}
                    errorMsg={
                      !!fc.errors.lastName
                        ? t('common:validation.lastNameMissing', 'Last name is mandatory')
                        : undefined
                    }
                  />
                </Row>

                <LabelInput
                  placeholder={t('common:general.email', 'Email')}
                  type="email"
                  autoComplete="username"
                  name="email"
                  id="email"
                  labelText={t('common:general.email', 'Email')}
                  ref={fc.register({
                    validate: {
                      isEmail: value => {
                        let valueToTest = value.replace(/ /g, '')
                        const regex = EMAIL_REGEX
                        if (!!!valueExists(valueToTest) || regex.test(valueToTest)) {
                          return true
                        } else {
                          return `${t('common:validation.invalidEmail', 'Invalid email')}`
                        }
                      }
                    },
                    required: t('common:validation.emailMandatory', 'Email is mandatory')
                  })}
                  disabled={isHealthInspector}
                  errorMsg={!!fc.errors.email ? fc.errors.email!.message : undefined}
                />
                {emailChanged ? (
                  <div>
                    <LabelInput
                      placeholder={t('users:placeholders.repeatNewEmail', 'Repeat new email')}
                      type="email"
                      autoComplete="off"
                      name="confirmEmail"
                      id="confirmEmail"
                      labelText={t('users:labels.repeatNewEmail', 'Repeat new email')}
                      ref={fc.register({
                        validate: {
                          isEmail: value => {
                            let valueToTest = value.replace(/ /g, '')
                            const regex = EMAIL_REGEX
                            if (!!!valueExists(valueToTest) || regex.test(valueToTest)) {
                              return true
                            } else {
                              return `${t('common:validation.invalidEmail', 'Invalid email')}`
                            }
                          }
                        },
                        required: t('common:validation.emailMandatory', 'Email is mandatory')
                      })}
                      disabled={isHealthInspector}
                      errorMsg={!!fc.errors.confirmEmail ? fc.errors.confirmEmail!.message : undefined}
                    />
                    <H6>
                      {t(
                        'users:messages.emailCorrect',
                        'Please make sure that the new email address is correct, since you will be using it later for login to this service.'
                      )}
                    </H6>
                  </div>
                ) : (
                  undefined
                )}

                <LabelInput
                  placeholder={t('common:general.phoneNumber', 'Phone number')}
                  autoComplete="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  labelText={t('common:general.phoneNumber', 'Phone number')}
                  ref={fc.register({
                    validate: {
                      isPhoneNumber: value => {
                        let valueToTest = value.replace(/ /g, '')
                        const regex = /^(\+){1}(\d){7,15}$/i
                        if (!!!valueExists(valueToTest) || regex.test(valueToTest)) {
                          return true
                        } else {
                          return `${t(
                            'common:validation.phoneFormat',
                            'Phone number must be in international format and start with + character'
                          )}`
                        }
                      }
                    }
                  })}
                  disabled={isHealthInspector}
                  errorMsg={!!fc.errors.phoneNumber ? fc.errors.phoneNumber!.message : undefined}
                />
                <Select
                  options={[
                    {option: t('common:general.lang.english', 'English'), id: UserLanguage.en},
                    {option: t('common:general.lang.finnish', 'Finnish'), id: UserLanguage.fi},
                    {option: t('common:general.lang.swedish', 'Swedish'), id: UserLanguage.sv},
                    {option: t('common:general.lang.norwegian', 'Norwegian'), id: UserLanguage.no}
                  ]}
                  id="language"
                  label={t('common:general.language', 'Language')}
                  ref={fc.register}
                  nativeProps={{defaultValue: UserLanguage.en}}
                  returnId
                />

                {!isEditing && (
                  <P>
                    {t(
                      'users:messages.passwordInvitation',
                      'A password is created automatically when an invitation is sent.'
                    )}
                  </P>
                )}
                <div className="empty" />

                {isEditingSelf && !state.me?.user.ssoProvisioned ? (
                  <>
                    <H2>{t('users:labels.changePassword', 'Change password')}</H2>
                    <div className="empty" />

                    {isEditing && (
                      <LabelInput
                        placeholder={t('users:labels.currentPassword', 'Current password')}
                        type="password"
                        autoComplete="current-password"
                        name="currentPassword"
                        id="currentPassword"
                        labelText={t('users:labels.currentPassword', 'Current password')}
                        ref={fc.register}
                        errorMsg={fc.errors.currentPassword && t(fc.errors.currentPassword.message || '')}
                      />
                    )}
                    <LabelInput
                      placeholder={t('users:labels.newPassword', 'New password')}
                      type="password"
                      autoComplete="new-password"
                      name="newPassword"
                      id="newPassword"
                      labelText={t('users:labels.newPassword', 'New password')}
                      ref={fc.register}
                      onChange={() => {
                        fc.setPasswordEdited(true)
                      }}
                    />
                    <H6>
                      {t(
                        'common:validation.passwordLength',
                        'Your password must contain minimum 8 characters, lowercase letters, and uppercase letters.'
                      )}
                    </H6>
                    <LabelInput
                      placeholder={t('users:labels.repeatNewPassword', 'Repeat new password')}
                      type="password"
                      autoComplete="off"
                      name="confirmNewPassword"
                      id="confirmNewPassword"
                      labelText={t('users:labels.repeatNewPassword', 'Repeat new password')}
                      ref={fc.register}
                      onChange={() => {
                        fc.setPasswordEdited(true)
                      }}
                      errorMsg={passwordErrorMessage}
                    />
                  </>
                ) : (
                  undefined
                )}
              </Baseline>
              {
                /* 
                  Please note:  
                  1. Report subscriptions section is only visible to superusers for testing purposes. This is to ensure that the feature works as intended before making it available to all users.
                  2. When tested and confirmed that it works as intended, remove the superuser check to make it visible to all users
                */
              }
              {isEditing && isSuperuser && (
                <ReportSubscriptionsColumn>
                  <Baseline margin="0.75rem">
                    <h3>{t('users:labels.subscribeScheduledReport', 'Subscribe to a scheduled report')}</h3>
                    <span>{t('users:labels.subscribeScheduledReportDescription', 'Once a week')}</span>
                    {reportOptions.map((reportOption) => {
                      const isEnabled = selectedReportTypes.includes(reportOption.type)

                      return (
                        <Row key={reportOption.type} style={{justifyContent: 'space-between'}}>
                          <P>{t(reportOption.labelKey, reportOption.fallback)}</P>
                          <ColoredTogglerButton
                            checked={isEnabled}
                            disabled={isLoadingReportSubscriptions}
                            option1Text={t('common:togglerValues.off', 'Off')}
                            option2Text={t('common:togglerValues.on', 'On')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              toggleReportType(reportOption.type, !!e.target.checked)
                            }
                          />
                        </Row>
                      )
                    })}
                  </Baseline>
                </ReportSubscriptionsColumn>
              )}

              {!!savedData && isEditingSelf && <EditUserAccountSettingsUserGroups userData={savedData} />}
              <div className="empty"></div>
            </EditDetailsGrid>
            <ButtonRowWrap>
              {showResetPassword ? (
                <div>
                  <Button variant="secondary" negative onClick={() => fc.handleResetPassword()}>
                    {t('users:labels.resetPassword', 'Reset password')}
                  </Button>
                </div>
              ) : (
                <div className="empty" />
              )}

              <ButtonRowGrid>
                <Button variant="secondary" negative onClick={() => fc.cancel()}>
                  {t('common:buttons.cancel', 'Cancel')}
                </Button>
                <Button
                  disabled={
                    !!passwordErrorMessage ||
                    !!emailErrorMessage ||
                    !!fc.errors.phoneNumber ||
                    !isEmpty(fc.errors) ||
                    rolesEmpty ||
                    !isEditingAllowed
                  }
                  variant="secondary"
                  onClick={() => {
                    const payload = {orgState, selectedBuSites, isManager: !!isManager, siteIdsOnly}
                    isEditing
                      ? fc.update(
                          payload,
                          selectedRole,
                          isEditingSelf,
                          isSuperuser ? selectedReportTypes : undefined
                        )
                      : fc.create(payload, selectedRole)
                  }}
                >
                  {isEditing
                    ? t('common:buttons.save', 'Save')
                    : t('common:buttons.saveAndInvite', 'Save and send invitation')}
                </Button>

                <GridSpan span={2}>
                  {!isEditingAllowed && (
                    <P>
                      {t(
                        'users:messages.noPermissions',
                        'You do not have sufficient permissions to edit this user. User is a member of organisation that you do not have access to'
                      )}
                    </P>
                  )}
                </GridSpan>
              </ButtonRowGrid>
            </ButtonRowWrap>
          </InvisibleContainer>
        </Baseline>
      </MainContent>
      {!isEditingSelf && (
        <NarrowRightSidebar>
          <AdjustableContainer padding="2rem">
            <Baseline>
              <P variant="small">
                {t(
                  'users:messages.selectUserRole',
                  'Select user role and organisation hierarchy level for the user. The role defines the permissions the user has on selected organisation levels, locations or sites.'
                )}
              </P>
              <RadioButtonGroup
                label={t('users:labels.userRole', 'User role')}
                options={[
                  {value: 'BU', label: t('users:labels.staff', 'Staff')},
                  {value: 'LM', label: t('users:labels.manager', 'Manager')},
                  {value: 'HI', label: t('users:labels.healthInspector', 'Health Inspector')}
                ]}
                selectedValue={selectedRole}
                onChange={handleRoleChange}
              />

              {rolesEmpty && (
                <Text2 style={{color: colors.system.red}}>
                  {t('common:validation.locationRequired', 'You must choose at least one location')}
                </Text2>
              )}
              <div>
                {(selectedRole === 'BU' || selectedRole === 'HI') && (
                  <UserSiteSelector data={chain} selected={selectedBuSites} onToggle={handleBUToggle} />
                )}
                {selectedRole === 'LM' && (
                  <ManagerLevelCheckbox
                    writableIds={writableIds}
                    data={chain}
                    level="chains"
                    onlySites={!isManager}
                    onClick={handleManagementLevelClick}
                    orgState={orgState}
                  />
                )}
              </div>
            </Baseline>
          </AdjustableContainer>
        </NarrowRightSidebar>
      )}
    </MainLayoutWithoutStretch>
  )
}

export default EditUserAccountSettingsDetails
